import { signOut } from "firebase/auth";
import { auth as firebaseAuth } from "firebaseClient";
import { useAuth } from "providers/Auth";
import { isInternalSession, SessionClaims } from "providers/Auth/internalSession";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/** How long a session opened from Poseidon lasts, from its sign-in time. */
export const INTERNAL_SESSION_MAX_MS = 2 * 60 * 60 * 1000;

/** How often the clock is re-checked while the tab is open, in case the timer was held back. */
const RECHECK_INTERVAL_MS = 30 * 1000;

export const INTERNAL_SESSION_ENDED_MESSAGE =
  "Internal session ended after 2 hours. Open the portal again from Poseidon.";

/**
 * When a Poseidon session must end, in epoch milliseconds; null for any other session.
 * `auth_time` is the sign-in time in seconds and a token refresh does not move it, so the
 * limit runs from when the operator opened the portal. A Poseidon session without one is
 * treated as already over rather than unlimited.
 */
export function internalSessionEndsAt(claims: SessionClaims): number | null {
  if (!isInternalSession(claims)) return null;
  const authTime = Number(claims?.auth_time);
  return Number.isFinite(authTime) && authTime > 0 ? authTime * 1000 + INTERNAL_SESSION_MAX_MS : 0;
}

/**
 * Ends a Poseidon session two hours after it was opened: at once when that time has passed,
 * otherwise on a timer. A Firebase session refreshes indefinitely, so without this an internal
 * ADMIN session would outlive the visit. Browsers throttle or freeze timers in background tabs
 * and a sleeping machine stops them, so the clock is also re-checked when the tab becomes
 * visible or focused and on a coarse interval. It ends each session once (effects run twice in
 * StrictMode, and several checks can fire together), and navigates before signing out because
 * the sign-out unmounts the page that holds this hook. Client-side only: the security rules do
 * not check it.
 */
export function useInternalSessionLimit(): void {
  const { auth } = useAuth()!;
  const navigate = useNavigate();
  const endsAt = internalSessionEndsAt(auth?.claims as SessionClaims);
  const endedFor = useRef<number | null>(null);

  useEffect(() => {
    if (endsAt === null) return undefined;
    const end = () => {
      if (endedFor.current === endsAt) return;
      endedFor.current = endsAt;
      navigate("/internal/open", { replace: true, state: { ended: true } });
      void signOut(firebaseAuth);
    };
    const check = () => {
      if (Date.now() >= endsAt) end();
    };
    const remaining = endsAt - Date.now();
    if (remaining <= 0) {
      end();
      return undefined;
    }
    const timer = setTimeout(end, remaining);
    const interval = setInterval(check, RECHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [endsAt, navigate]);
}
